import Head from "next/head";
import Link from "next/link";
import Image from "next/image";
import NextScript from "next/script";
import { Box, Stack } from "@mui/material";
import { useEffect, useState } from "react";

import { SSText } from "src/components/base/components/SSText";
import { SSButton } from "src/components/base/components/SSButton";

import { routes } from "src/shared/routes";
import { constants } from "src/shared/constants";
import { createJsxLink } from "src/utils/create-jsx-link";
import { useSSTheme } from "src/components/reusable/hooks/useSSTheme";
import { useRouterExt } from "src/components/reusable/hooks/useRouterExt";
import { useGenericModal } from "src/components/reusable/components/GenericModal";
import { useTeamConfig } from "src/components/reusable/components/TeamConfigProvider";

import type { TTeamConfig } from "src/shared/types/teamConfig.types";
import type { TRegistrationCardData } from "src/shared/types/globalStotre.types";

import seatMapImage from "public/images/miamiopen_upgrades/miamiopen_upgrades_seat_map.png";

const RegistratinCardSecondaryButton = () => {
  const [isLoading, setIsLoading] = useState(false);

  const onClick = () => {
    setIsLoading(true);
    window.location.href = "https://www.miamiopen.com/order-of-play/";
  };

  return (
    <SSButton
      size="large"
      fullWidth
      variant={"outlined"}
      onClick={onClick}
      loading={isLoading}
      disabled={isLoading}
      sx={{ fontWeight: 500 }}
    >
      Today&apos;s Order of Play
    </SSButton>
  );
};

export const REGISTRATION_CARDS: TRegistrationCardData[] = [
  {
    title: "Seat Upgrades Tailored to You",
    description: (
      <>
        Elevate your Miami Open experience by upgrading your grounds pass to a
        reserved seat on Stadium Court featuring our marquee matches each day.
        Click below to view options.
      </>
    ),
    authRedirectPage: routes.home,
    buttonVariant: "contained",
    secondaryButton: <RegistratinCardSecondaryButton />,
  },
];

export const FAQS: TTeamConfig["LANGUAGES"]["FAQS"] = [
  {
    question: "How come I don't see seat upgrades available for purchase?",
    answer:
      "Upgrades are only eligible for those with a game ticket already. If you do not have a ticket for the game, please go to Rockies Ticket Information | Colorado Rockies to purchase.",
  },
  {
    question: "Will there be seat upgrades available for every game?",
    answer:
      "Upgrades are subject to availability, please check back in on the day of the game to see eligible offers.",
  },
  {
    question: "Where are my tickets?",
    answer:
      "After successful purchase, seat upgrades will be delivered within your Ballpark app. Please note that these tickets are not eligible for entry into Coors Field.",
  },
  {
    question: "Where will my seats be located?",
    answer:
      "Seats will be located in the sections noted in your seat upgrade offer.",
  },
  {
    question: "Can these tickets be transferred to a friend?",
    answer: "Seat upgrades are non transferrable and not eligible for resale.",
  },
  {
    question: "Can these tickets be transferred to a friend?",
    answer: "Seat upgrades are non transferrable and not eligible for resale.",
  },
  {
    question: "Can my seats be changed?",
    answer:
      "Once tickets have been sent to your account, they cannot be changed. Please head to Ticket Resolution for any assistance you may require.",
  },
];

export const TERMS_AND_CONDITION_ADD_ON = () => {
  const theme = useSSTheme();
  return (
    <>
      By purchasing, I agree to the Miami Open&apos;s{" "}
      <Link
        href="https://www.miamiopen.com/ticket-terms-and-conditions/"
        target="_blank"
        style={{
          cursor: "pointer",
          color: theme.colors.teamPrimary,
          textDecoration: "underline",
          textUnderlineOffset: "0.2em",
          fontWeight: "bold",
        }}
      >
        Ticket Terms and Conditions
      </Link>
      .
    </>
  );
};

export const REGISTER_TERMS_AND_CONDITIONS = () => (
  <>
    By creating an account, I agree to the Miami Open&apos;s{" "}
    {createJsxLink("Terms", "https://www.miamiopen.com/terms-of-use/")} and Hard
    Rock Stadium&apos;s{" "}
    {createJsxLink(
      "Terms",
      "https://www.hardrockstadium.com/terms-and-conditions/",
    )}{" "}
    and understand that personal information will be used as described in the
    Miami Open&apos;s{" "}
    {createJsxLink(
      "Privacy Policy",
      "https://www.miamiopen.com/privacy-policy/",
    )}{" "}
    and Hard Rock Stadium&apos;s{" "}
    {createJsxLink(
      "Privacy Policy",
      "https://www.hardrockstadium.com/privacy-policy/",
    )}
    . Once registered, you will be able to access the Miami Open&apos;s
    preference center to control content, offers, and more that you receive from
    the Miami Open. You also confirm that you have read and agree to the
    platform&apos;s{" "}
    {createJsxLink(
      "Terms & Conditions",
      "https://www.attend.tech/terms-of-use",
    )}{" "}
    and{" "}
    {createJsxLink("Privacy Policy", "https://www.attend.tech/privacy-policy")}.
  </>
);

export const DISCLAIMER = () => {
  const theme = useSSTheme();
  const modal = useGenericModal();

  function handleClick() {
    modal.set({
      isOpen: true,
      description: (
        <Box
          sx={{
            width: "100%",
            margin: "0 auto",
            maxWidth: "550px",
            aspectRatio: "1/1",
            position: "relative",
          }}
        >
          <Image
            fill
            alt="Arena Map"
            style={{ objectFit: "cover" }}
            src={seatMapImage.src}
          />
        </Box>
      ),
      title: "Arena Map",
      isConfirmButtonHidden: true,
      onClickClose: async () => modal.reset(),
      onClickCloseIcon: async () => modal.reset(),
    });
  }

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={0.5}
      justifyContent="center"
    >
      To view a map of the arena, click{" "}
      <Stack direction="row" alignItems="center">
        <SSText
          variant="body"
          onClick={handleClick}
          style={{
            cursor: "pointer",
            color: theme.colors.teamPrimary,
            textDecoration: "underline",
            textUnderlineOffset: "0.2em",
            fontWeight: "bold",
          }}
        >
          here
        </SSText>
        .
      </Stack>
    </Stack>
  );
};

export const AnalyticsHeader = () => {
  const router = useRouterExt();
  const { TEAM_SCHEMA } = useTeamConfig();

  useEffect(() => {
    const handleRouteChange = (path: string) => {
      try {
        const url = `${window.location.origin}/${path}`;
        if (window?.fbq) {
          window?.fbq("track", "PageView");
        }
        if (window?.gtag) {
          window?.gtag("event", "page_view", {
            page_location: url,
          });
        }
      } catch (err) {
        console.log(err);
      }
    };
    router.events.on("routeChangeComplete", handleRouteChange);
    return () => {
      router.events.off("routeChangeComplete", handleRouteChange);
    };
  }, [router.events]);

  return (
    <Head>
      <NextScript
        async
        nonce={process.env.NEXT_PUBLIC_CSP_NONCE}
        src="https://www.googletagmanager.com/gtag/js?id=G-JB642QERR2"
      />

      <script
        id={`${TEAM_SCHEMA}-analytics-inline`}
        nonce={process.env.NEXT_PUBLIC_CSP_NONCE}
        dangerouslySetInnerHTML={{
          __html: `
            window.dataLayer = window.dataLayer || [];
            function gtag() {
              dataLayer.push(arguments);
            }
            gtag('js', new Date());
            gtag('config', 'G-JB642QERR2');
            window.addEventListener("${constants.customEvents.purchaseSuccess}", function(e) {
              const { cartRes, checkoutRes } = e.detail;
              gtag('event', 'purchase', {
                'send_to': 'G-JB642QERR2',
                'value': cartRes.totalAmount,
                'currency': 'USD',
                'transaction_id': checkoutRes.orderNumber
              });
              console.log('gtag analytics purchase event fired.');
            });
        `,
        }}
      />

      <script
        defer
        nonce={process.env.NEXT_PUBLIC_CSP_NONCE}
        id={`${TEAM_SCHEMA}-pixel-script`}
        dangerouslySetInnerHTML={{
          __html: `
              !function(f,b,e,v,n,t,s)
              {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
              n.callMethod.apply(n,arguments):n.queue.push(arguments)};
              if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
              n.queue=[];t=b.createElement(e);t.async=!0;
              t.src=v;s=b.getElementsByTagName(e)[0];
              s.parentNode.insertBefore(t,s)}(window, document,'script',
              'https://connect.facebook.net/en_US/fbevents.js');
              fbq('init', '270186807673102');
              fbq('track', 'PageView');
              window.addEventListener("${constants.customEvents.purchaseSuccess}", function(e) {
                try{
                  const { cartRes, checkoutRes, passes } = e.detail;

                  let content_name = "";
                  if(cartRes.products && cartRes.products.length) {
                    content_name = "${TEAM_SCHEMA}_single_game_purchases";
                  };
                  if(cartRes.passes && cartRes.passes.length && passes.length ) {
                    const passDetail = passes.find((pass) => pass.passId == cartRes.passes[0].passId);
                    content_name = passDetail.passTitle;
                  };

                  fbq('track', 'Purchase', {
                    content_name: content_name, 
                    value: String(cartRes.totalAmount), 
                    currency: 'USD', 
                    order_id: checkoutRes.orderNumber,
                  });
                  console.log('meta pixel purchase event fired.');
                
                } catch(error){
                  console.log('meta pixel purchase event error:', error); 
                }
              });
            `,
        }}
      />
      <noscript>
        <img
          height="1"
          width="1"
          style={{ display: "none" }}
          src="https://www.facebook.com/tr?id=270186807673102&ev=PageView&noscript=1"
        />
      </noscript>
    </Head>
  );
};
